/**
 * RevenueShareDisplayPage — `/franchise/revenue-share`.
 *
 * Wraps the `/v1/franchise/revenue/franchise/:id/snapshots` and `/aggregate`
 * endpoints. Lets the operator pick a franchise + date range, shows the
 * aggregated revenue + commission split, and lists the underlying snapshots.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  aggregateRevenue,
  listFranchises,
  listRevenueSnapshots,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoMinusDays(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function RevenueShareDisplayPage(): JSX.Element {
  const [franchiseId, setFranchiseId] = useState('');
  const [fromDate, setFromDate] = useState(() => isoMinusDays(30));
  const [toDate, setToDate] = useState(() => isoToday());

  const franchises = useQuery({
    queryKey: ['franchise', 'rev-share', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const agg = useQuery({
    queryKey: ['franchise', 'rev-share', 'agg', franchiseId, fromDate, toDate],
    queryFn: () => aggregateRevenue(franchiseId, { fromDate, toDate }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const snapshots = useQuery({
    queryKey: ['franchise', 'rev-share', 'snaps', franchiseId, fromDate, toDate],
    queryFn: () => listRevenueSnapshots(franchiseId, { fromDate, toDate, pageSize: 50 }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const denied =
    (agg.isError && agg.error instanceof ApiError &&
      (agg.error.status === 401 || agg.error.status === 403)) ||
    (snapshots.isError && snapshots.error instanceof ApiError &&
      (snapshots.error.status === 401 || snapshots.error.status === 403));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Revenue share"
        description="Per-franchise revenue snapshots and hierarchical aggregation."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Franchise</label>
          {franchises.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <select
              value={franchiseId}
              onChange={(e) => setFranchiseId(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm min-w-[260px]"
            >
              <option value="">Select…</option>
              {franchises.data?.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.legalName} ({f.franchiseNumber})
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
      </div>

      {denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseRevenue/read</code> required.
        </DeferredNotice>
      ) : null}

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise to view its revenue share.
        </p>
      ) : agg.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : agg.isError ? (
        <DeferredNotice title="Aggregate unavailable">{(agg.error as Error).message}</DeferredNotice>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard title="Total revenue" value={formatNgn(agg.data?.totalRevenue ?? '0')} />
          <KPICard title="Total commission" value={formatNgn(agg.data?.totalCommission ?? '0')} />
          <KPICard title="Snapshots" value={String(agg.data?.snapshotCount ?? 0)} />
          <KPICard title="Descendants" value={String(agg.data?.descendantCount ?? 0)} />
        </div>
      )}

      {franchiseId.length > 0 ? (
        snapshots.isPending ? null : snapshots.isError ? null : (snapshots.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No snapshots in the selected range.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Revenue</th>
                  <th className="text-right px-4 py-2">Commission</th>
                  <th className="text-right px-4 py-2">Txns</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {snapshots.data?.items.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs text-muted">{s.date}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs">
                      {formatNgn(s.totalRevenue)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs">
                      {formatNgn(s.commissionAmount)}
                    </td>
                    <td className="px-4 py-2 text-right text-xs">{s.txnCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}
